/**
 * Modèle « Elo + valeur d'effectif ». Mélange l'Elo réel avec une note dérivée
 * de la valeur marchande des effectifs (Transfermarkt), validé par backtest
 * (poids ~0,25 optimal : +0,77 % de RPS vs Elo seul, sur 121 amicaux/qualifs).
 *
 * La note valeur est remise à l'ÉCHELLE de l'Elo (mêmes moyenne et écart-type)
 * pour rester compatible avec le mapping Elo→buts. Les nations sans valeur
 * connue gardent leur Elo pur.
 *
 * Données fraîches servies par le serveur (`/data/squad-values.json`, rafraîchi
 * chaque jour) ; à défaut (dev / hors-ligne) on retombe sur la graine committée
 * `src/data/squad-values.json` (règle 8 : jamais d'écran cassé).
 */
import seedDoc from "../data/squad-values.json";
import { initialElos } from "./data.ts";
import type { EloTable } from "../engine/types.ts";

/** Part de la valeur d'effectif dans le mélange (optimum du backtest). */
export const VALUE_BLEND_WEIGHT = 0.25;

const mean = (xs: number[]): number => xs.reduce((a, b) => a + b, 0) / (xs.length || 1);
const std = (xs: number[]): number => {
  const m = mean(xs);
  return Math.sqrt(xs.reduce((a, b) => a + (b - m) ** 2, 0) / (xs.length || 1));
};

/** Table Elo mélangée à la valeur d'effectif. */
export function valueBlendedElos(
  squadValues: Readonly<Record<string, number>>,
  w: number = VALUE_BLEND_WEIGHT,
  base: EloTable = initialElos,
): EloTable {
  const codes = Object.keys(base);
  const eloMean = mean(codes.map((c) => base[c] ?? 0));
  const eloStd = std(codes.map((c) => base[c] ?? 0)) || 1;
  const logs = codes
    .filter((c) => (squadValues[c] ?? 0) > 0)
    .map((c) => Math.log(squadValues[c] as number));
  const lMean = mean(logs);
  const lStd = std(logs) || 1;
  const out: Record<string, number> = {};
  for (const c of codes) {
    const sv = squadValues[c] ?? 0;
    const eloC = base[c] ?? 0;
    out[c] =
      sv > 0 ? (1 - w) * eloC + w * (eloMean + eloStd * ((Math.log(sv) - lMean) / lStd)) : eloC;
  }
  return out;
}

const seedValues = (seedDoc as { values: Record<string, number> }).values;
let cached: EloTable = valueBlendedElos(seedValues);
let cachedRawValues: Record<string, number> = seedValues;

/** Table Elo+valeur courante : la graine au départ, fraîche après refresh. */
export function currentValueElos(): EloTable {
  return cached;
}

/** Valeurs d'effectif BRUTES (€) par code FIFA — graine au départ, fraîches après
 *  refresh. Sert l'affichage (colonne « Valeur » du classement Elo). */
export function currentSquadValues(): Readonly<Record<string, number>> {
  return cachedRawValues;
}

/** Meilleur effort : récupère les valeurs fraîches du serveur ; sinon graine. */
export async function refreshValueElos(): Promise<void> {
  try {
    const res = await fetch("/data/squad-values.json", { cache: "no-store" });
    if (!res.ok) return;
    const doc = (await res.json()) as { values?: Record<string, number> };
    if (doc.values && Object.keys(doc.values).length >= 30) {
      cachedRawValues = doc.values;
      cached = valueBlendedElos(doc.values);
    }
  } catch {
    /* dev sans serveur / hors-ligne → on garde la graine committée */
  }
}
